import {
  CreatedScimToken,
  ReadScimGroupMapping,
  ReadScimSettings,
  ReadScimToken,
  UpdateScimGroupMapping,
} from '@tinct/core-api-models';
import { ApiClient } from '../api-client/api-client';
import {
  ApiCollectionResponse,
  FieldQuery,
  GetCollectionQueryParams,
} from '../types';

export interface GetScimTokensStaticFilter {
  organizationId: string;
}

/**
 * SCIM provisioning of the workspace: whether the identity provider may manage its members, the
 * address it calls, and the bearer tokens it calls with. Workspace admins only (403 for editors and
 * viewers), and 404 while the workspace is not entitled to single sign-on or SSO is switched off.
 */
export class ScimEndpoint {
  constructor(private client: ApiClient) {}

  async getScimSettings(
    organizationId: string,
    fields?: FieldQuery<ReadScimSettings>,
  ): Promise<ReadScimSettings> {
    return this.client.getById<ReadScimSettings>(
      `/api/v1/organizations/${organizationId}/scim`,
      fields,
    );
  }

  /** Idempotent. 409 (5701) while a prerequisite is unmet: its message says which. */
  async enableScim(organizationId: string): Promise<ReadScimSettings> {
    return this.client.post<ReadScimSettings>(
      `/api/v1/organizations/${organizationId}/scim/enablement`,
    );
  }

  /**
   * Idempotent. Deprovisions nobody: the identity provider's requests are refused (403) and its
   * tokens stop working until SCIM is switched on again.
   */
  async disableScim(organizationId: string): Promise<ReadScimSettings> {
    return this.client.delete<ReadScimSettings>(
      `/api/v1/organizations/${organizationId}/scim/enablement`,
    );
  }

  /** The unrevoked tokens, oldest first, two at most. None carries its value. */
  async getScimTokens(
    params: GetCollectionQueryParams<ReadScimToken, GetScimTokensStaticFilter>,
  ): Promise<ApiCollectionResponse<ReadScimToken>> {
    const { staticParams, fields, offset, limit, filters } = params;
    return this.client.getCollection<ReadScimToken>(
      `/api/v1/organizations/${staticParams?.organizationId}/scim/tokens`,
      fields,
      { offset, limit, ...filters },
    );
  }

  /**
   * Generates a token (201). The answer is the only one that ever carries its value (`token`); it
   * cannot be read again. 409 (5702) when two tokens exist already: revoke one first.
   */
  async createScimToken(organizationId: string): Promise<CreatedScimToken> {
    return this.client.post<CreatedScimToken>(
      `/api/v1/organizations/${organizationId}/scim/tokens`,
    );
  }

  /** 204. 404 when the token is unknown or already revoked. */
  async revokeScimToken(organizationId: string, tokenId: string): Promise<void> {
    return this.client.delete<void>(
      `/api/v1/organizations/${organizationId}/scim/tokens/${tokenId}`,
    );
  }

  /**
   * Every group the directory pushed, mapped to a role or not: a plain array, no field selection and
   * no pagination. Admins only.
   */
  async getScimGroupMappings(
    organizationId: string,
  ): Promise<ReadScimGroupMapping[]> {
    return this.client.getById<ReadScimGroupMapping[]>(
      `/api/v1/organizations/${organizationId}/scim/group-mappings`,
    );
  }

  /**
   * Maps a group to a role. 400 (5703) when mapping to `admin` without `confirmAdmin: true`; 409
   * (5704) when the change would leave the workspace without an admin.
   */
  async updateScimGroupMapping(
    organizationId: string,
    groupId: string,
    data: UpdateScimGroupMapping,
  ): Promise<ReadScimGroupMapping> {
    return this.client.put<ReadScimGroupMapping>(
      `/api/v1/organizations/${organizationId}/scim/group-mappings/${groupId}`,
      data,
    );
  }

  /** 204, idempotent: the group maps to nothing any more. 409 (5704) when no admin would be left. */
  async deleteScimGroupMapping(
    organizationId: string,
    groupId: string,
  ): Promise<void> {
    return this.client.delete<void>(
      `/api/v1/organizations/${organizationId}/scim/group-mappings/${groupId}`,
    );
  }
}
